import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { catchError, of, take } from 'rxjs';
import { Category, OrderItem } from 'src/app/stores/stores';
import { LocationService } from '../../location.service';
import {
  getCurrentCartContent,
  getSelectedStoreCategories,
} from '../+state/stores.selectors';
import { CustomerCheckoutRecommendationsCacheService } from './customer-checkout-recommendations-cache.service';
import { CustomerCheckoutRecommendationsService } from './customer-checkout-recommendations.service';
import { basketCoveredCategoryIds } from './customer-checkout-recommendations.helpers';

/**
 * Orchestrates the cesta → recommendations → checkout transition for the customer flow (#9303).
 *
 * <p>Called from {@code CheckoutService.proceedToCheckout()} after it has computed the final
 * destination URL but before it actually navigates. The coordinator fetches the store's active
 * recommendation rules; when the list is empty (master toggle off, no rules, or fetch failure),
 * it falls back to the original destination so the existing flow is unaffected. When at least
 * one rule exists, it pushes the customer onto the first recommendation page passing the queue
 * of remaining rule ids and the final destination URL, so each subsequent page knows where to
 * go next at proceed-time. Each per-rule page re-evaluates the basket just-in-time on
 * activation (see {@code CustomerCheckoutRecommendationsPageComponent}).
 *
 * <p>The full rules list is also pushed via the router state so each page can resolve its
 * rule's metadata (question, configured category order) without a second API call — the
 * router state survives across the chained navigations triggered by the recommendation pages
 * themselves.
 */
@Injectable({ providedIn: 'root' })
export class CustomerCheckoutRecommendationsFlowCoordinator {
  /** Prevents re-entrant calls (e.g. from a double-click) while an active-rules fetch is in flight. */
  private inFlight = false;

  constructor(
    private readonly service: CustomerCheckoutRecommendationsService,
    private readonly cache: CustomerCheckoutRecommendationsCacheService,
    private readonly router: Router,
    private readonly locationService: LocationService,
    private readonly store: Store,
  ) {}

  /**
   * Look up the store's active rules; on a non-empty result, navigate to the first rule's
   * recommendation view with the rest queued and the final destination preserved. Otherwise
   * navigate straight to the destination the customer would have reached before this feature
   * existed.
   *
   * <p>Navigation uses the storefront's hash-view convention
   * ({@code <path>#checkout-recommendations?…}, mirroring {@code #cart} / {@code #checkout}) via
   * {@link LocationService#base_url}, so the recommendation step lives inside the existing customer
   * route and never depends on a separate Angular route — which only resolved under the
   * {@code /public} mount and broke on the subdomain storefront (#9303).
   *
   * @param storeId      the current store id, used as the API path scope and the view param
   * @param destination  the {@code base_url} destination key ({@code 'cart'} | {@code 'checkout'})
   *                     the customer was about to reach; used both when no rules exist and chained
   *                     through the rule views so the last view lands there
   * @param languageId   optional current customer locale id, forwarded to the active-rules API so
   *                     the question is translated server-side
   */
  startFlowOrNavigate(opts: {
    storeId: number;
    destination: string;
    languageId?: number;
    /** Hash-view key to navigate to when the customer clicks Back from the recommendations
     *  page. Defaults to 'catalog'. Passed through the URL so every chained rule page
     *  knows the correct back destination without relying on browser history. */
    back?: string;
  }): void {
    // Guard against re-entrant calls (e.g. a double-click firing two simultaneous fetches).
    // The second call would race the first: both could independently decide where to navigate
    // and the second completion could land the user on checkout without showing the
    // recommendation screen.
    if (this.inFlight) {
      return;
    }
    this.inFlight = true;

    // A new flow (the customer pressed "Proceed to checkout") starts a fresh recommendation
    // visit: clear any per-rule items cached from a previous flow so each page recomputes
    // against the current basket. The cache also self-clears on order-uuid change, which is the
    // primary safeguard for kiosk sessions that never refresh.
    this.cache.reset();
    this.service
      .loadActiveRules(opts.storeId, opts.languageId)
      .pipe(
        take(1),
        catchError(() => of([])),
      )
      .subscribe((rules) => {
        this.inFlight = false;
        if (!rules || rules.length === 0) {
          this.router.navigateByUrl(
            this.locationService.base_url(opts.destination),
          );
          return;
        }
        // Pre-filter rules whose configured categories are all already in the basket so the
        // page never mounts for a rule the backend will answer with []. Without this the
        // customer sees the recommendation page flash for ~0.5-1 s (the API round-trip) before
        // onProceed() advances them — the "screens randomly skipped" bug (#10374 Bug 1). The
        // page keeps its own empty-result skip as a safety net for genuinely empty responses.
        const covered = this.basketCoveredCategoryIds();
        const eligibleRules =
          covered.size > 0
            ? rules.filter(
                (r) =>
                  !r.categoryIdsInOrder.length ||
                  r.categoryIdsInOrder.some((id) => !covered.has(id)),
              )
            : rules; // no coverage info → don't filter (same reference)
        if (eligibleRules.length === 0) {
          this.router.navigateByUrl(
            this.locationService.base_url(opts.destination),
          );
          return;
        }
        // Persist rule metadata in the singleton cache so the page component can
        // recover rule.question on back-navigation (component is destroyed/recreated
        // and Angular's router state is unavailable on popstate) — #10374.
        this.cache.setRules(eligibleRules);
        const [first, ...rest] = eligibleRules;
        const target = this.buildRecommendationsUrl({
          storeId: opts.storeId,
          ruleId: first.ruleId,
          remainingRuleIds: rest.map((r) => r.ruleId).join(','),
          destination: opts.destination,
          back: opts.back ?? 'catalog',
        });
        this.router
          .navigateByUrl(target, {
            state: { rules: eligibleRules, rule: first },
          })
          .then((ok) => {
            if (!ok) {
              // Defensive: a guard or duplicate-URL block rejected the navigation. Fall
              // back to the original destination so the customer is never stuck on the
              // catalog "Proceed" click — the feature degrades to its pre-#9303 behaviour
              // rather than silently swallowing the transition.
              this.router.navigateByUrl(
                this.locationService.base_url(opts.destination),
              );
            }
          });
      });
  }

  /**
   * Synchronously read the NgRx store (BehaviorSubject-backed, so {@code take(1)} fires inline)
   * to build the set of catalog category ids already covered by the customer's basket at flow
   * start. Delegates to the shared {@link basketCoveredCategoryIds} helper so this pre-filter and
   * the page component's per-rule resolve read basket coverage identically. Returns an empty set
   * when the cart or catalog state is not yet available — the caller then skips filtering.
   */
  private basketCoveredCategoryIds(): Set<number> {
    let cartItems: OrderItem[] = [];
    let catalogCategories: Category[] | null = null;
    this.store
      .pipe(select(getCurrentCartContent), take(1))
      .subscribe((items) => {
        cartItems = items ?? [];
      });
    this.store
      .pipe(select(getSelectedStoreCategories), take(1))
      .subscribe((cats) => {
        catalogCategories = cats;
      });
    return basketCoveredCategoryIds(cartItems, catalogCategories);
  }

  /**
   * Build the hash-view URL for a recommendation rule, following the storefront's
   * {@code <currentPath>#<view>?<query>} convention via {@link LocationService#base_url}. The
   * query carries the per-view params (store, rule, the remaining rule-id queue, and the final
   * destination key) so each chained view knows what to resolve and where to go next.
   *
   * <p>{@code base_url} also re-applies any active {@code orderUuid/…} / {@code l/…} URL prefix,
   * which the previous standalone-route navigation bypassed.
   */
  private buildRecommendationsUrl(params: {
    storeId: number;
    ruleId: number;
    remainingRuleIds: string;
    destination: string;
    back: string;
  }): string {
    const query = new URLSearchParams({
      storeId: String(params.storeId),
      ruleId: String(params.ruleId),
      remainingRuleIds: params.remainingRuleIds,
      destination: params.destination,
      back: params.back,
    }).toString();
    return this.locationService.base_url(`checkout-recommendations?${query}`);
  }
}
